import { useUiFromSpecContext } from '../../EditorContextProvider'
import { useDrag, useDragLayer } from 'react-dnd'
import { DndElementsType, multiLanguage } from '../dndElements'
import { useState } from 'react'
import { MdKeyboardArrowDown } from 'react-icons/md'
import { useScrollShadow } from '../utils/handleOnScrollSearch'
import clsx from 'clsx'
import { getLanguageLabel } from '../EditInput/EditInputFields/utils/getLanguageLabel'
import { useTranslation } from '../../hooks/useTranslation'
import { toTitleCase } from '@/utils/functions/toTitleCase'
import { ItemTypes } from '../dndTypes'

interface DropResult {
  xpath: string
  index: number
}

export const InputListDragable = (props: { componentPanelClassname?: string }) => {
  const { dndElements } = useUiFromSpecContext()
  const { t } = useTranslation()
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [searchQuery, setSearchQuery] = useState<string>('')
  const scrollShadowRef = useScrollShadow()

  if (!dndElements) return <div className="text-center py-4 text-gray-500">No elements available</div>

  // Filter elements based on search query
  const filteredElements = searchQuery.trim()
    ? dndElements.filter((element) => {
      const query = searchQuery.toLowerCase()

      // If label is a string, search directly
      if (typeof element.label === 'string') {
        return element.label.toLowerCase().includes(query)
      }

      // If label is an object with language keys, search in all language values
      if (typeof element.label === 'object' && element.label !== null) {
        return Object.values(element.label).some(
          (labelValue) => typeof labelValue === 'string' && labelValue.toLowerCase().includes(query)
        )
      }

      return false
    })
    : dndElements

  const grouped = filteredElements.reduce<Record<string, DndElementsType[]>>((acc, curr: DndElementsType) => {
    if (!acc[curr.type_element]) acc[curr.type_element] = []
    acc[curr.type_element].push(curr)
    return acc
  }, {})

  const toggleCollapse = (type: string) => {
    setCollapsed((prev) => ({ ...prev, [type]: !prev[type] }))
  }

  return (
    <div
      className={clsx(
        !props.componentPanelClassname && 'overflow-y-auto h-[calc(100vh-240px)] px-1',
        props.componentPanelClassname
      )}
    >
      <div className="mb-2 sticky top-0 bg-white z-10 transition-shadow duration-200" ref={scrollShadowRef}>
        <input
          type="text"
          className="w-full text-sm p-2 border rounded focus:outline-none focus:ring-0"
          placeholder={t(`componentPanel.searchPlaceholder`, undefined, 'Search elements...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      {Object.keys(grouped).length === 0 && searchQuery && (
        <div className="text-center py-4 text-gray-500">No elements found matching "{searchQuery}"</div>
      )}
      {Object.entries(grouped).map(([type, items]) => (
        <div key={type} className="mb-4">
          <div className="flex justify-between items-center cursor-pointer mb-2" onClick={() => toggleCollapse(type)}>
            <h4 className="text-lg font-semibold capitalize">
              {t(`inputElements.typeElements.${type}`, undefined, type)}
            </h4>
            <MdKeyboardArrowDown className={`transition-transform ${collapsed[type] ? 'rotate-180' : ''}`} />
          </div>
          {!collapsed[type] && (
            <div className="flex flex-wrap items-center gap-2">
              {items.map((item) => {
                const elementType = typeof item.label === 'string' ? item.label : getLanguageLabel(item.label as multiLanguage, 'en') || 'Unknown'
                return <DragableItem element={{ ...item }} key={`${type}-${elementType.replace(" ", "_").toLowerCase()}`}  />
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
export const DragableItem = ({ element}: { element: DndElementsType }) => {
  const { appendNewElementToSpec, language } = useUiFromSpecContext()
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.PALETTE_ELEMENT,
    item: { ...element },
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult<DropResult>()

      if (item && dropResult) {
        const index = dropResult.index + 1
        if (isNaN(index)) {
          appendNewElementToSpec && appendNewElementToSpec({ ...element.config }, dropResult.xpath.split('.'))
        } else {
          appendNewElementToSpec && appendNewElementToSpec({ ...element.config }, dropResult.xpath.split('.'), index)
        }
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      handlerId: monitor.getHandlerId(),
    }),
  }))

  return (
    /* @ts-ignore */
    <div
      ref={drag}
      className={clsx(
        'border rounded cursor-grab active:cursor-grabbing aspect-square flex flex-col justify-center items-center h-16 w-16 transition-all duration-150',
        isDragging
          ? 'opacity-40 scale-95 border-blue-400 bg-blue-50 shadow-inner'
          : 'hover:bg-gray-100 hover:border-gray-400 hover:shadow-sm'
      )}
      title={toTitleCase(getLanguageLabel(element.label as multiLanguage, language))}
    >
      <div className="h-min w-full p-2 flex flex-col justify-center items-center text-center text-[22px] space-y-1">
        {element?.icon}
        <span className="text-center text-[10px]">
          {toTitleCase(getLanguageLabel(element.label as multiLanguage, language))}
        </span>
      </div>
    </div>
  )
}

// Re-exported for backward compatibility — use ItemTypes.PALETTE_ELEMENT / CANVAS_ELEMENT in new code
export { ItemTypes }

/**
 * Returns whether a PALETTE_ELEMENT is currently being dragged.
 * Can be used by any component that needs to show/hide drop zones.
 */
export const useIsPaletteElementDragging = () => {
  const { isDragging } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging() && monitor.getItemType() === ItemTypes.PALETTE_ELEMENT,
  }))
  return isDragging
}

/**
 * Returns whether a CANVAS_ELEMENT is currently being dragged.
 */
export const useIsCanvasElementDragging = () => {
  const { isDragging } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging() && monitor.getItemType() === ItemTypes.CANVAS_ELEMENT,
  }))
  return isDragging
}
